import React, { useEffect, useMemo, useState } from "react";
import { accessApi } from "../../services/accessApi";

export default function ITPermissionsPage() {
  const [rows, setRows] = useState([]);
  const [modules, setModules] = useState([]);

  const [q, setQ] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const [form, setForm] = useState({
    code: "",
    name: "",
    module_code: "",
    description: "",
    is_active: 1,
    reason: "",
  });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError("");

      const [permissionsRes, modulesRes] = await Promise.all([
        accessApi.getPermissions({
          q: q || undefined,
          module_code: moduleFilter || undefined,
          is_active: statusFilter === "" ? undefined : statusFilter,
        }),
        accessApi.getModules(),
      ]);

      setRows(permissionsRes?.data?.data || []);
      setModules(modulesRes?.data?.data || []);
    } catch (err) {
      console.error("ITPermissionsPage loadAll error:", err);
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load permissions."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      loadPermissionsOnly();
    }, 250);

    return () => clearTimeout(t);
  }, [q, moduleFilter, statusFilter]);

  const loadPermissionsOnly = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await accessApi.getPermissions({
        q: q || undefined,
        module_code: moduleFilter || undefined,
        is_active: statusFilter === "" ? undefined : statusFilter,
      });

      setRows(res?.data?.data || []);
    } catch (err) {
      console.error("ITPermissionsPage loadPermissionsOnly error:", err);
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load permissions."
      );
    } finally {
      setLoading(false);
    }
  };

  const moduleOptions = useMemo(() => {
    return [...modules].sort((a, b) =>
      String(a.name || a.code).localeCompare(String(b.name || b.code))
    );
  }, [modules]);

  const openCreate = () => {
    setEditingRow(null);
    setForm({
      code: "",
      name: "",
      module_code: moduleOptions[0]?.code || "",
      description: "",
      is_active: 1,
      reason: "",
    });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setForm({
      code: row.code || "",
      name: row.name || "",
      module_code: row.module_code || "",
      description: row.description || "",
      is_active: Number(row.is_active) === 1 ? 1 : 0,
      reason: "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingRow(null);
  };

  const savePermission = async () => {
    try {
      if (!form.code.trim()) {
        alert("Code is required.");
        return;
      }
      if (!form.name.trim()) {
        alert("Name is required.");
        return;
      }
      if (!form.module_code) {
        alert("Module is required.");
        return;
      }

      setSaving(true);

      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        module_code: form.module_code,
        description: form.description?.trim() || null,
        is_active: Number(form.is_active) === 1 ? 1 : 0,
        reason: form.reason?.trim() || null,
      };

      if (editingRow?.id) {
        await accessApi.updatePermission(editingRow.id, payload);
      } else {
        await accessApi.createPermission(payload);
      }

      setShowModal(false);
      setEditingRow(null);
      await loadPermissionsOnly();
    } catch (err) {
      console.error("savePermission error:", err);
      alert(
        err?.response?.data?.message ||
          err.message ||
          "Failed to save permission."
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    try {
      await accessApi.updatePermissionStatus(row.id, {
        is_active: Number(row.is_active) === 1 ? 0 : 1,
        reason:
          Number(row.is_active) === 1
            ? "Deactivated from IT Admin"
            : "Activated from IT Admin",
      });

      await loadPermissionsOnly();
    } catch (err) {
      console.error("toggleStatus error:", err);
      alert(
        err?.response?.data?.message ||
          err.message ||
          "Failed to update permission status."
      );
    }
  };

  if (loading && !rows.length && !error) {
    return (
      <div className="p-6 text-black dark:text-white">
        Loading permissions...
      </div>
    );
  }

  if (error && !rows.length) {
    return <div className="p-6 text-red-600 dark:text-red-400">{error}</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-black dark:text-white">
            Permissions
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage system permissions and connect them to application modules.
          </p>
        </div>

        <button
          onClick={openCreate}
          className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium"
        >
          + Add Permission
        </button>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search permissions..."
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          />

          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          >
            <option value="">All modules</option>
            {moduleOptions.map((m) => (
              <option key={m.id} value={m.code}>
                {m.name} ({m.code})
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          >
            <option value="">All statuses</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900 px-4 py-3 text-red-700 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead className="bg-gray-50 dark:bg-neutral-800">
              <tr className="border-b border-gray-200 dark:border-neutral-700">
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Code
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Name
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Module
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Description
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Status
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/60 transition"
                >
                  <td className="p-3 text-black dark:text-white font-medium">
                    {row.code}
                  </td>
                  <td className="p-3 text-black dark:text-white">
                    {row.name}
                  </td>
                  <td className="p-3 text-black dark:text-white">
                    <div className="font-medium">{row.module_name || "-"}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {row.module_code}
                    </div>
                  </td>
                  <td className="p-3 text-gray-700 dark:text-gray-300">
                    {row.description || "-"}
                  </td>
                  <td className="p-3 text-black dark:text-white">
                    {Number(row.is_active) === 1 ? "Active" : "Inactive"}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => openEdit(row)}
                        className="px-3 py-1 rounded-lg border border-gray-300 dark:border-neutral-700"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() => toggleStatus(row)}
                        className="px-3 py-1 rounded-lg border border-gray-300 dark:border-neutral-700"
                      >
                        {Number(row.is_active) === 1
                          ? "Deactivate"
                          : "Activate"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="p-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    No permissions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-3xl rounded-2xl bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-black dark:text-white">
                {editingRow ? "Edit Permission" : "Add Permission"}
              </h2>
              <button
                onClick={closeModal}
                className="px-3 py-1 rounded-lg border border-gray-300 dark:border-neutral-700"
              >
                Close
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Code
                </label>
                <input
                  value={form.code}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, code: e.target.value }))
                  }
                  disabled={!!editingRow}
                  placeholder="e.g. mail.read"
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white disabled:opacity-60"
                />
              </div>

              <div>
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Name
                </label>
                <input
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g. Read Mail"
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Module
                </label>
                <select
                  value={form.module_code}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      module_code: e.target.value,
                    }))
                  }
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                >
                  <option value="">Select module</option>
                  {moduleOptions.map((m) => (
                    <option key={m.id} value={m.code}>
                      {m.name} ({m.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Status
                </label>
                <select
                  value={form.is_active}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_active: Number(e.target.value),
                    }))
                  }
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={4}
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Reason
                </label>
                <input
                  value={form.reason}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, reason: e.target.value }))
                  }
                  placeholder="Optional audit reason"
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
              >
                Cancel
              </button>
              <button
                onClick={savePermission}
                disabled={saving}
                className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}